<template>
  <v-layout>
    <div class="col">
      <v-store-user-info/>
      <payment-breadcrumb :step="2"/>
      <article>
        <header>
          <h1>{{ $t('paymentSelection.title') }}</h1>
        </header>
      </article>
      <article>
        <div class="inner">
          <form action="" method="post" class="payment">
            <fieldset>
              <div class="form-row inline-label">
                <label>{{ $t('paymentSelection.totalExclDiscountLabel') }}</label>
                <strong class="price">{{ $n(totalProductAmount.amount, 'currency') }}</strong>
              </div>
              <div class="form-row inline-label">
                <div class="form-column">
                  <v-discount-code-row/>
                </div>
              </div>
              <div v-if="hasDiscount()" class="form-row inline-label">
                <div class="form-column">
                  <label>{{ $t('paymentSelection.discountOverviewLabel') }}</label>
                  <v-discount-overview :cart-product-items="cartProductItems"/>
                </div>
              </div>
              <div v-if="false && /* temporarily disabled, not part of CR */ hasCombiDealDiscount()" class="form-row inline-label">
                <div class="form-column">
                  <label>{{ $t('paymentSelection.combiDealDiscountOverviewLabel') }}</label>
                  <v-combi-deal-discount-overview :cart-product-items="cartProductItems"/>
                </div>
              </div>
              <div class="form-row inline-label">
                <div class="form-column">
                  <label>{{ $t('paymentSelection.payDirectlyLabel') }}</label>
                  <strong id="totalAmountToPay" class="price">
                    {{ $n(directTotalPaymentAmount ? directTotalPaymentAmount.amount : 0, 'currency') }}
                  </strong>
                </div>
              </div>

              <v-payment-method-selection-row
                v-show="!isDirectPayableAmountZero()"
                :validation="$v.selectedPaymentMethod"
                :payment-methods="paymentMethods"
                :show-required-indicator="isStoreUser"
                :selected-payment-method="selectedPaymentMethod"/>

              <v-payment-option-selection-row
                v-if="!isStoreUser && selectedPaymentMethod && selectedPaymentMethod.options.length > 0"
                v-show="!isDirectPayableAmountZero()"
                :validation="$v.selectedPaymentOption"
                :payment-method="selectedPaymentMethod"
                :selected-payment-option="selectedPaymentOption"/>

              <br clear="all">

              <v-store-user-payment-confirmed-row
                v-if="isStoreUser"
                :validation="$v.storeUserPaymentConfirmed"
                :store-user-payment-confirmed="storeUserPaymentConfirmed"
                :selected-payment-method="selectedPaymentMethod"
                @toggleConfirmPayment="onToggleConfirmPayment"/>

              <v-term-overview
                v-if="shouldTermOverviewBeVisible() && shopType === 'NAL'"
                :cart-product-items="cartProductItems"
                :payment-configurations="paymentConfigurations"/>

              <v-abt-term-overview
                v-if="shouldTermOverviewBeVisible() && shopType === 'ABT'"
                :cart-product-items="cartProductItems"/>

              <v-store-user-product-table
                v-if="isStoreUser"
                :cart-product-items="cartProductItems"/>
            </fieldset>
          </form>
        </div>
        <p>* {{ $t('generic.label-required-fields') }}</p>
      </article>
      <article>
        <div class="inner">
          <nav class="flow-controls">
            <router-link
              :to="{name: paymentRoute, params: {lang: 'nl'} }"
              class="button alt">
              {{ $t('paymentSelection.previous') }}
            </router-link>
            <a
              :class="['button', {disabled: serviceError!==null}]"
              href="#"
              @click.prevent="confirmPayment()"
            >{{ $t('paymentSelection.next') }}</a>
          </nav>
        </div>
      </article>
    </div>
    <v-modal v-if="paymentFailedNotification"
             id="paymentFailedNotificationModal"
             @close="closePaymentFailedNotificationModal()">
      <h2>&nbsp;</h2>
      <p>{{ $t('paymentSelection.paymentFailedNotification') }}</p>
    </v-modal>
    <v-modal v-if="ineligibleForTermPaymentNotification"
             id="ineligibleForTermPaymentNotificationModal"
             @close="closeIneligibleForTermPaymentNotificationModal()">
      <h2>&nbsp;</h2>
      <p>{{ $t('paymentSelection.ineligibleForTermPaymentNotification') }}</p>
    </v-modal>
    <v-modal v-if="invalidPaymentAmountNotification"
             id="invalidPaymentAmountNotificationModal"
             @close="closeInvalidPaymentAmountNotificationModal()">
      <h2>&nbsp;</h2>
      <p>{{ $t('paymentSelection.invalidPaymentAmountNotification') }}</p>
    </v-modal>
    <v-modal v-if="createOrderFailedNotification"
             id="createOrderFailedNotification"
             @close="closeCreateOrderFailedNotificationModal()">
      <h2>&nbsp;</h2>
      <p>{{ $t('paymentSelection.createOrderFailedNotification') }}</p>
    </v-modal>
    <v-modal v-if="serviceErrorNotificationVisible"
             id="errorNotificationModal"
             @close="closeErrorNotificationModal">
      <h2>&nbsp;</h2>
      <p>{{ $t('generic.service-error') }}</p>
    </v-modal>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { SET_ORDER_CREATED, CLEAR_ITEMS, RESET_ORDER_STATUS } from '../store/modules/cart/mutation-types'
import {
  ORDER_STATUS_ACCEPTED, ORDER_STATUS_DRAFT,
  ORDER_STATUS_FAILED_PAYMENT
} from '../order-api/order-api-client-constants'
import { PAYMENT_METHOD_FREE } from '../types/payment-method'
import authenticationMixin from '../mixins/authentication'
import bodyClassMixin from '../mixins/body-class'
import validationErrorFocusMixin from '../mixins/validation-error-focus'
import VLayout from '../layouts/PaymentFlow'
import VPaymentMethodSelectionRow from '../components/payment-selection/PaymentMethodSelectionRow'
import VPaymentOptionSelectionRow from '../components/payment-selection/PaymentOptionSelectionRow'
import VDiscountCodeRow from '../components/payment-selection/DiscountCodeRow'
import VDiscountOverview from '../components/payment-selection/DiscountOverview'
import VCombiDealDiscountOverview from '../components/payment-selection/CombiDealDiscountOverview'
import VTermOverview from '../components/payment-selection/TermOverview'
import VAbtTermOverview from '../components/payment-selection/AbtTermOverview'
import VStoreUserPaymentConfirmedRow from '../components/payment-selection/StoreUserPaymentConfirmedRow'
import VStoreUserInfo from '../components/user/StoreUserInfo'
import VStoreUserProductTable from '../components/payment-selection/StoreUserProductTable'
import PaymentBreadcrumb from '../components/PaymentBreadcrumb'
import VModal from '../components/modals/Modal'

export default {
  name: 'PaymentSelectionPage',
  components: {
    VLayout,
    VPaymentMethodSelectionRow,
    VPaymentOptionSelectionRow,
    VDiscountCodeRow,
    VDiscountOverview,
    VCombiDealDiscountOverview,
    VTermOverview,
    VAbtTermOverview,
    VStoreUserPaymentConfirmedRow,
    VStoreUserInfo,
    VStoreUserProductTable,
    PaymentBreadcrumb,
    VModal
  },
  mixins: [
    validationMixin,
    authenticationMixin,
    bodyClassMixin,
    validationErrorFocusMixin
  ],
  data () {
    return {
      paymentFailedNotification: false,
      ineligibleForTermPaymentNotification: false,
      invalidPaymentAmountNotification: false,
      createOrderFailedNotification: false,
      createOrderFailedRedirectRoute: this.paymentRoute,
      serviceError: null,
      serviceErrorNotificationVisible: false
    }
  },
  validations () {
    return {
      selectedPaymentMethod: {
        required,
        validPaymentMethod: function (selectedPaymentMethod) {
          return selectedPaymentMethod
            ? this.paymentMethods.find(candidate => candidate.title === selectedPaymentMethod.title) !== undefined
            : false
        }
      },
      selectedPaymentOption: this.selectedPaymentMethod && this.selectedPaymentMethod.options.length > 0
        ? { required }
        : {},
      storeUserPaymentConfirmed: this.isStoreUser
        ? { required }
        : {}
    }
  },
  computed: {
    ...mapGetters({
      paymentMethods: 'payment/paymentMethods',
      paymentConfigurations: 'payment/paymentConfigurations',
      totalProductAmount: 'cart/totalProductAmountWithVat',
      directTotalPaymentAmount: 'cart/directTotalPaymentAmount',
      selectedPaymentMethod: 'payment/selectedPaymentMethod',
      selectedPaymentOption: 'payment/selectedPaymentOption',
      totalDiscountAmount: 'cart/totalDiscountAmount',
      totalCombiDealDiscountAmount: 'cart/totalCombiDealDiscountAmount',
      cartProductItems: 'cart/cartProductItems',
      payInTerms: 'cart/payInTerms',
      paymentRoute: 'cart/paymentRoute',
      overviewRoute: 'cart/overviewRoute',
      shopType: 'shop/type',
      orderCustomerNumber: 'cart/orderCustomerNumber',
      userCustomerNumber: 'user/customerNumber'
    }),
    ...mapState({
      storeUserPaymentConfirmed: state => state.payment.storeUserPaymentConfirmed,
      orderStatus: state => state.cart.orderStatus,
      orderCreated: state => state.cart.orderCreated
    })
  },
  watch: {
    directTotalPaymentAmount: function () {
      if (!this.isStoreUser) {
        this.updateSelectedPaymentMethodBasedOnDirectPayableAmount()

        if (this.isDirectPayableAmountZero()) {
          this.clearPayInTermsValue()
        }
      }
    }
  },
  mounted: async function () {
    if (!this.cartProductItems || this.cartProductItems.length === 0) {
      this.$router.push({ name: this.overviewRoute }).catch(() => {})
    }

    await this.recreateOrderIfPaymentInitiated()

    const isOrderCreated = (this.orderCreated !== null) || await this.createOrder()

    if (!isOrderCreated) {
      return
    }

    try {
      await this.$store.dispatch('payment/updatePaymentMethods')

      if (!this.selectedPaymentMethod && !this.isStoreUser) {
        this.updateSelectedPaymentMethodBasedOnDirectPayableAmount()
      }

      if (this.orderStatus && this.orderStatus.status === ORDER_STATUS_FAILED_PAYMENT) {
        this.paymentFailedNotification = true
      }

      await this.$store.dispatch('cart/updatePaymentSpecification')
      this.serviceErrorNotificationVisible = false
      this.serviceError = null
    } catch (e) {
      this.serviceErrorNotificationVisible = true
      this.serviceError = e
    }
  },
  methods: {
    userIsOrderOwner: function () {
      if (this.userCustomerNumber) {
        return this.orderCustomerNumber === this.userCustomerNumber
      }

      return true
    },
    shouldTermOverviewBeVisible: function () {
      return this.isPaymentInTerms() && !this.isStoreUser
    },
    isPaymentInTerms: function () {
      return this.payInTerms &&
        this.paymentConfigurations &&
        Object
          .values(this.paymentConfigurations)
          .filter(paymentConfiguration => paymentConfiguration.paymentTermConfigurations.length > 0).length > 0
    },
    hasCombiDealDiscount: function () {
      return this.totalCombiDealDiscountAmount && this.totalCombiDealDiscountAmount.amount !== 0
    },
    hasDiscount: function () {
      return this.totalDiscountAmount && this.totalDiscountAmount.amount !== 0
    },
    onToggleConfirmPayment: function () {
      this.$store.dispatch('payment/setStoreUserConfirmPayment', !this.storeUserPaymentConfirmed)
    },
    isDirectPayableAmountZero: function () {
      return this.directTotalPaymentAmount && this.directTotalPaymentAmount.amount === 0
    },
    updateSelectedPaymentMethodBasedOnDirectPayableAmount: function () {
      if (this.isDirectPayableAmountZero()) {
        this.$store.dispatch(
          'payment/setSelectedPaymentMethod',
          this.paymentMethods.find(candidate => candidate.title === PAYMENT_METHOD_FREE)
        )
      }
    },
    clearPayInTermsValue: function () {
      this.$store.dispatch('cart/clearPayInTerms')
    },
    confirmPayment: function () {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        if (this.userIsOrderOwner()) {
          if (this.isStoreUser) {
            if (confirm(this.$t('paymentSelection.storeUserConfirmPaymentAlert'))) {
              this.initiatePayment()
            }
          } else {
            this.initiatePayment()
          }
        } else {
          this.serviceErrorNotificationVisible = true
        }
      }
    },
    initiatePayment: async function () {
      if (this.directTotalPaymentAmount && this.directTotalPaymentAmount.amount >= 0) {
        try {
          await this.recreateOrderIfPaymentInitiated()
          const initiatePaymentResponse = await this.$container.orderApiClient.initiatePayment(this.$store.getters['cart/orderPurchase'])

          if ('errors' in initiatePaymentResponse && initiatePaymentResponse.errors.length > 0) {
            if (initiatePaymentResponse.errors.find(error => error.code === '2aa69544-3871-42f6-a233-9ff4ba27df37')) {
              this.openIneligibleForTermPaymentNotificationModal()
            } else {
              throw new Error(initiatePaymentResponse.errors.map(error => error.message).join(', '))
            }
          } else if ('redirectUrl' in initiatePaymentResponse) {
            window.location.assign(initiatePaymentResponse.redirectUrl)
          }
        } catch (error) {
          this.paymentFailedNotification = true
        }
      } else {
        this.invalidPaymentAmountNotification = true
      }
    },
    createOrder: async function () {
      try {
        const orderCreated = await this.$container.orderApiClient.createOrder(this.$store.getters['cart/order'])
        this.$store.commit('cart/' + SET_ORDER_CREATED, orderCreated)
        return true
      } catch (error) {
        this.createOrderFailedNotification = true
        if (error.errors && error.errors.length > 0) {
          this.$store.commit('cart/' + CLEAR_ITEMS)
          this.createOrderFailedRedirectRoute = this.overviewRoute
        }
        return false
      }
    },
    closePaymentFailedNotificationModal: function () {
      this.paymentFailedNotification = false
      /* Reset order status if payment was failed to let the user try to pay again */
      this.$store.commit('cart/' + RESET_ORDER_STATUS)
    },
    openIneligibleForTermPaymentNotificationModal: function () {
      this.ineligibleForTermPaymentNotification = true
    },
    closeIneligibleForTermPaymentNotificationModal: function () {
      this.ineligibleForTermPaymentNotification = false
      /* Reset order status if payment was failed to let the user try to pay again */
      this.$store.commit('cart/' + RESET_ORDER_STATUS)
    },
    closeInvalidPaymentAmountNotificationModal: function () {
      this.invalidPaymentAmountNotification = false
    },
    closeCreateOrderFailedNotificationModal: function () {
      this.createOrderFailedNotification = false
      this.$router.push({ name: this.createOrderFailedRedirectRoute }).catch(() => {})
    },
    closeErrorNotificationModal () {
      this.serviceErrorNotificationVisible = false
    },
    async recreateOrderIfPaymentInitiated () {
      if (await this.isOrderCreatedAndPaymentInitiated()) {
        await this.$store.dispatch('cart/' + 'clearOrderCreated')
        await this.createOrder()
      }
    },
    async isOrderCreatedAndPaymentInitiated () {
      const order = this.$store.getters['cart/order']
      if (order && order.id && order.token) {
        const response = await this.$container.orderApiClient.getOrderStatus(order.id, order.token)
        return response && !(response.status === ORDER_STATUS_ACCEPTED || response.status === ORDER_STATUS_DRAFT)
      }

      return false
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/PaymentSelection.vue